import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { ApiError } from '../../lib/api.ts';
import {
  deleteFilmSource,
  fetchFilm,
  fetchTranscript,
  transcribeFilm,
  unarchiveEntity,
} from '../../lib/queries.ts';
import type { FileState, Film, Segment, Transcript } from '../../lib/types.ts';
import {
  ArchivedChip,
  Capability,
  CapabilityBlock,
  formatBytes as formatFileSize,
  isFreed,
  shortDate,
} from '../../components/archive/index.ts';
import { FilmArchiveDialog, useFilmChildCounts } from './FilmArchiveDialog.tsx';
import { MomentsSection } from './MomentsSection.tsx';

function formatMmSs(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function pad(n: number, width: number): string {
  return n.toString().padStart(width, '0');
}

function srtTime(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  const ms = Math.round((sec - Math.floor(sec)) * 1000);
  return `${pad(h, 2)}:${pad(m, 2)}:${pad(s, 2)},${pad(ms, 3)}`;
}

function segmentsToTxt(segments: Segment[]): string {
  return segments.map((s) => `[${formatMmSs(s.startSec)}] ${s.text}`).join('\n');
}

function segmentsToSrt(segments: Segment[]): string {
  return segments
    .map(
      (s, i) =>
        `${i + 1}\n${srtTime(s.startSec)} --> ${srtTime(s.endSec)}\n${s.text}`,
    )
    .join('\n\n');
}

function downloadBlob(name: string, content: string, mime: string): void {
  // BOM prefix forces Windows Notepad to read as UTF-8 (otherwise it guesses cp1251).
  const blob = new Blob(['﻿', content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function formatBytes(b: number | null): string {
  if (b === null) return '—';
  const mb = b / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${Math.round(mb)} MB`;
}

export function FilmDetailPage(): React.ReactElement {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);

  const filmQuery = useQuery({
    queryKey: ['films', id],
    queryFn: () => fetchFilm(id!),
    enabled: Boolean(id),
    refetchInterval: (q) => {
      const film = q.state.data?.film;
      return film?.status === 'transcribing' || film?.status === 'inventorying' ? 3_000 : false;
    },
  });

  const transcriptQuery = useQuery({
    queryKey: ['transcript', id],
    queryFn: () => fetchTranscript(id!),
    enabled: Boolean(id) && filmQuery.data?.film.status === 'transcribed',
    retry: false,
  });

  const transcribeMutation = useMutation({
    mutationFn: () => transcribeFilm(id!),
    onMutate: () => {
      // Optimistic flip so the spinner appears instantly. Polling (enabled when
      // status === 'transcribing') will overwrite with the real state on the
      // next 3-second cycle, by which point the worker has picked up the job.
      queryClient.setQueryData<{ film: Film } | undefined>(['films', id], (old) =>
        old ? { ...old, film: { ...old.film, status: 'transcribing', errorMessage: null } } : old,
      );
    },
    onError: () => {
      // Mutation failed (likely a 400 status race) — pull real state back.
      void queryClient.invalidateQueries({ queryKey: ['films', id] });
    },
  });

  const deleteSourceMutation = useMutation({
    mutationFn: () => deleteFilmSource(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['films', id] });
    },
  });

  const unarchiveMutation = useMutation({
    mutationFn: () => unarchiveEntity('film', id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['films'] });
    },
  });

  const children = useFilmChildCounts(id ?? '');

  if (filmQuery.isLoading) return <div style={{ padding: 16 }}>{t('common.loading')}</div>;
  if (filmQuery.isError) {
    return (
      <div style={{ padding: 16, color: 'var(--hot)' }}>
        {t('films.detail.loadError', { message: (filmQuery.error as Error).message })}
      </div>
    );
  }
  if (!filmQuery.data) {
    return <div style={{ padding: 16 }}>{t('films.detail.noFilm')}</div>;
  }

  const film: Film = filmQuery.data.film;
  // Ось файла: только 'freed' — потеря способности. 'absent' (исходника в R2
  // нет, фильм локальный) способностей не отнимает.
  const file: FileState = filmQuery.data.file;
  const sourceFreed = isFreed(file);
  const transcript: Transcript | undefined = transcriptQuery.data?.transcript;
  const canTranscribe = ['inventoried', 'transcribed', 'error'].includes(film.status);

  return (
    <>
      <div className="page-head">
        <div>
          <Link to="/films" style={{ color: 'var(--text-3)', fontSize: 12, textDecoration: 'none' }}>
            {t('films.detail.back')}
          </Link>
          <h1 style={{ marginTop: 6, display: 'flex', alignItems: 'baseline', gap: 10 }}>
            {film.parsedTitle ?? film.rawName}
            <ArchivedChip archivedAt={film.archivedAt} />
          </h1>
          <div className="sub">
            {film.parsedYear ?? '—'} · {film.folderCategory} · {film.language} ·{' '}
            <span className={`pill ${film.status === 'transcribed' ? 'live' : ''}`}>
              {t(`films.status_${film.status}`)}
            </span>
          </div>
        </div>
        {!film.archivedAt && (
          <div className="filters">
            <button type="button" onClick={() => setShowArchiveDialog(true)}>
              {t('films.archiveButton')}
            </button>
          </div>
        )}
      </div>

      {/* Архивный фильм: что с ним стало и что ещё можно сделать. Красное —
          только когда исходник реально освобождён (это невосстановимо). */}
      {film.archivedAt && (
        <div className="section">
          <div className="section-body">
            {sourceFreed ? (
              <div className="note err">
                {t('films.detail.archivedBannerFreed', {
                  since: shortDate(film.archivedAt),
                  date: shortDate(file.freedAt),
                })}
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={() => unarchiveMutation.mutate()}
                    disabled={unarchiveMutation.isPending}
                  >
                    {t('films.detail.unarchive')}
                  </button>
                </div>
              </div>
            ) : (
              <div className={file.freeAfter ? 'note warn' : 'note'}>
                {file.freeAfter
                  ? t('films.detail.archivedBanner', {
                      since: shortDate(film.archivedAt),
                      size: formatFileSize(file.sizeBytes) ?? '—',
                      date: shortDate(file.freeAfter),
                    })
                  : t('films.detail.archivedBannerNoSchedule', {
                      since: shortDate(film.archivedAt),
                    })}
                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => unarchiveMutation.mutate()}
                    disabled={unarchiveMutation.isPending}
                  >
                    {t('films.detail.unarchive')}
                  </button>
                  {film.sourceR2Key && !film.sourceDeletedAt && (
                    <button
                      type="button"
                      onClick={() => deleteSourceMutation.mutate()}
                      disabled={deleteSourceMutation.isPending}
                    >
                      {t('films.detail.freeSourceNow')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Что можно делать — механически из состояния файла, не из архивности. */}
      <div className="section">
        <div className="section-head">
          <h2>{t('films.detail.capabilities')}</h2>
        </div>
        <div className="section-body">
          <CapabilityBlock>
            <Capability
              available={!sourceFreed}
              title={t('archive.capability.film.cutClips')}
              why={
                sourceFreed
                  ? t('archive.capability.film.cutClipsNo', { date: shortDate(file.freedAt) })
                  : file.freeAfter
                    ? t('archive.capability.film.cutClipsOkUntil', {
                        date: shortDate(file.freeAfter),
                      })
                    : t('archive.capability.film.cutClipsOk')
              }
            />
            <Capability
              available
              title={t('archive.capability.film.transcript')}
              why={t('archive.capability.film.transcriptWhy')}
            />
            <Capability
              available
              title={t('archive.capability.film.children')}
              why={`${t('archive.dialog.film.clipsPart', { count: children.clips })} ${t(
                'archive.dialog.and',
              )} ${t('archive.dialog.film.variantsPart', { count: children.variants })} ${t(
                'archive.capability.film.childrenLive',
              )}`}
            />
          </CapabilityBlock>
        </div>
      </div>

      <div className="section">
        <div className="section-head">
          <h2>{t('films.detail.metadata')}</h2>
        </div>
        <div className="section-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {film.tmdbPosterUrl && (
            <img
              src={film.tmdbPosterUrl}
              alt=""
              style={{ width: 120, height: 180, objectFit: 'cover', borderRadius: 4 }}
            />
          )}
          <dl style={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 6 }}>
            <dt>{t('films.detail.metadataDuration')}</dt>
            <dd>{film.durationSec ? `${Math.floor(film.durationSec / 60)} min` : '—'}</dd>
            <dt>{t('films.detail.metadataSize')}</dt>
            <dd>{formatBytes(film.sizeBytes)}</dd>
            <dt>{t('films.detail.metadataContainer')}</dt>
            <dd>{film.container ?? '—'}</dd>
            <dt>{t('films.detail.metadataResolution')}</dt>
            <dd>{film.width != null && film.height != null ? `${film.width}×${film.height}` : '—'}</dd>
            <dt>{t('films.detail.metadataSrt')}</dt>
            <dd>{film.hasSrt ? film.srtPath : t('films.noSrt')}</dd>
            <dt>{t('films.detail.metadataLanguage')}</dt>
            <dd>{film.language}</dd>
          </dl>
          {film.tmdbOverview && (
            <div style={{ gridColumn: '1 / -1', color: 'var(--text-2)', fontSize: 13 }}>
              {film.tmdbOverview}
            </div>
          )}
          {film.errorMessage && (
            <div style={{ gridColumn: '1 / -1', color: 'var(--hot)', fontSize: 12 }}>
              {film.errorMessage}
            </div>
          )}
        </div>
      </div>

      {film.sourceR2Key && (
        <div className="section">
          <div className="section-head">
            <h2>{t('films.sourceSection')}</h2>
          </div>
          <div className="section-body">
            <dl style={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 6, marginBottom: 16 }}>
              <dt>{t('films.sourceR2Key')}</dt>
              <dd style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{film.sourceR2Key}</dd>
              {film.sourceSizeBytes !== null && (
                <>
                  <dt>{t('films.sourceSize')}</dt>
                  <dd>{formatBytes(film.sourceSizeBytes)}</dd>
                </>
              )}
              {film.sourceDeletedAt && (
                <>
                  <dt>{t('films.sourceDeletedAt')}</dt>
                  <dd>{new Date(film.sourceDeletedAt).toLocaleString()}</dd>
                </>
              )}
            </dl>
            {film.sourceDeletedAt ? (
              <span
                className="pill"
                style={{ fontSize: 11, background: 'rgba(255,61,110,0.1)', color: 'var(--hot)', border: '1px solid rgba(255,61,110,0.3)' }}
              >
                {t('films.sourceDeletedBadge')}
              </span>
            ) : (
              <div>
                {/* Освобождение ФАЙЛА (не записи): необратимо, поэтому confirm
                    остаётся — архивной модели это не противоречит. */}
                <button
                  onClick={() => {
                    if (confirm(t('films.sourceDeleteConfirm'))) {
                      deleteSourceMutation.mutate();
                    }
                  }}
                  disabled={deleteSourceMutation.isPending}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(255,61,110,0.3)',
                    color: 'var(--hot)',
                    padding: '4px 10px',
                    borderRadius: 6,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  {deleteSourceMutation.isPending ? '…' : t('films.detail.freeSourceNow')}
                </button>
                {deleteSourceMutation.error instanceof ApiError && (
                  <div style={{ marginTop: 6, color: 'var(--hot)', fontSize: 12 }}>
                    {deleteSourceMutation.error.message}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="section">
        <div className="section-head">
          <h2>{t('films.detail.transcriptSection')}</h2>
        </div>
        <div className="section-body">
          {film.status === 'transcribing' && (
            <div className="working">
              <span className="spinner" />
              {t('films.detail.transcribing')}
            </div>
          )}
          {!transcript && film.status !== 'transcribing' && (
            <div>
              <p style={{ color: 'var(--text-3)', fontSize: 12, marginBottom: 12 }}>
                {canTranscribe ? t('films.detail.noTranscript') : t('films.detail.transcribePendingHelp')}
              </p>
              <button
                className="primary"
                onClick={() => transcribeMutation.mutate()}
                disabled={!canTranscribe || transcribeMutation.isPending}
              >
                {transcribeMutation.isPending
                  ? t('films.detail.transcribing')
                  : t('films.detail.transcribeButton')}
              </button>
              {transcribeMutation.error instanceof ApiError && (
                <div style={{ marginTop: 8, color: 'var(--hot)', fontSize: 12 }}>
                  {transcribeMutation.error.message}
                </div>
              )}
            </div>
          )}
          {transcript && (
            <>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>
                {t('films.detail.transcriptSource', { source: transcript.source })} ·{' '}
                {t('films.detail.transcriptLanguage', { language: transcript.language })} ·{' '}
                {t('films.detail.transcriptDuration', { seconds: transcript.durationSec ?? '—' })} ·{' '}
                {t('films.detail.transcriptSegmentCount', { count: transcript.segments.length })}
              </div>
              <details>
                <summary style={{ cursor: 'pointer', marginBottom: 8 }}>
                  {t('films.detail.transcriptPreviewTitle')}
                </summary>
                <div
                  style={{
                    maxHeight: 320,
                    overflowY: 'auto',
                    fontSize: 12,
                    fontFamily: 'var(--mono)',
                    background: 'var(--bg-soft)',
                    padding: 10,
                    borderRadius: 4,
                  }}
                >
                  {transcript.segments.slice(0, 50).map((s: Segment, i) => (
                    <div key={i}>
                      <span style={{ color: 'var(--text-3)' }}>[{formatMmSs(s.startSec)}]</span>{' '}
                      {s.text}
                    </div>
                  ))}
                </div>
              </details>
              <div style={{ marginTop: 12, display: 'flex', gap: 16, alignItems: 'center' }}>
                <button
                  onClick={() =>
                    downloadBlob(
                      `${film.parsedTitle ?? 'transcript'}.txt`,
                      segmentsToTxt(transcript.segments),
                      'text/plain',
                    )
                  }
                  style={{
                    background: 'transparent',
                    color: 'var(--neon)',
                    border: '1px solid var(--line-strong)',
                    padding: '4px 10px',
                    borderRadius: 6,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  {t('films.detail.downloadTxt')}
                </button>
                <button
                  onClick={() =>
                    downloadBlob(
                      `${film.parsedTitle ?? 'transcript'}.srt`,
                      segmentsToSrt(transcript.segments),
                      'application/x-subrip',
                    )
                  }
                  style={{
                    background: 'transparent',
                    color: 'var(--neon)',
                    border: '1px solid var(--line-strong)',
                    padding: '4px 10px',
                    borderRadius: 6,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  {t('films.detail.downloadSrt')}
                </button>
                <span style={{ flex: 1 }} />
                <button
                  onClick={() => {
                    if (confirm(t('films.detail.retranscribeConfirm'))) {
                      transcribeMutation.mutate();
                    }
                  }}
                  disabled={transcribeMutation.isPending}
                >
                  {t('films.detail.retranscribeButton')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <MomentsSection
        film={film}
        defaultCount={film.maxClipCount}
        defaultDurMin={film.clipDurationMinSec}
        defaultDurMax={film.clipDurationMaxSec}
      />

      {showArchiveDialog && (
        <FilmArchiveDialog
          filmId={film.id}
          file={file}
          onClose={() => setShowArchiveDialog(false)}
        />
      )}
    </>
  );
}
